import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  Heading,
  useToast,
  Text,
  Image,
  SimpleGrid,
  Divider,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { APIProvider } from "@vis.gl/react-google-maps";

import { Gestiones, Institucion } from "../../../services/models/Institucion";
import ApiResponse from "../../../services/ApiResponse";
import config from "../../../MapConfig";
import { ApiValidationResponse } from "../../../services/models/ApiValidationResponse";
import handleApiError from "../../../utils/ApiErrorHandler";
import LogoFileInput from "../../../components/LogoFileInput";
import PhoneNumberInputArg from "../../../components/PhoneNumberInputArg";
import UrlInput from "../../../components/UrlInput";
import MapComponent from "../../../components/MapComponent";
import ImageFileInput from "../../../components/LogoFileInput";

const DEFAULT_COORDS = { lat: -29.14383470536903, lng: -59.26513140291266 };

// Definimos el estado para que sea compatible con lo que guardaremos
interface FormState {
  direccion: string;
  lat: number | null;
  lng: number | null;
  logoUrl: string;
  isSubmitting: boolean;
}

const InstitucionFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();

  const cueRef = useRef<HTMLInputElement>(null);
  const cueanexoRef = useRef<HTMLInputElement>(null);
  const nombreRef = useRef<HTMLInputElement>(null);
  const telRef = useRef<HTMLInputElement>(null);
  const paginaRef = useRef<HTMLInputElement>(null);
  const gestionRef = useRef<HTMLSelectElement>(null);
  const logoRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<FormState>({
    direccion: "",
    lat: DEFAULT_COORDS.lat,
    lng: DEFAULT_COORDS.lng,
    logoUrl: "",
    isSubmitting: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!id) return;
    const fetchInstitucion = async () => {
      const api = new ApiResponse<Institucion>();
      await api.useFetch(`instituciones/${id}`, "GET");

      if (api.data && !api.error) {
        const inst = api.data;

        // Seteamos los campos de los Refs
        if (nombreRef.current) nombreRef.current.value = inst.nombre;
        if (cueRef.current) cueRef.current.value = String(inst.cue);
        if (cueanexoRef.current)
          cueanexoRef.current.value = String(inst.cueanexo || "");
        if (telRef.current) telRef.current.value = inst.tel || "";
        if (paginaRef.current) paginaRef.current.value = inst.pagina || "";
        if (gestionRef.current) gestionRef.current.value = inst.gestion;

        // AQUÍ ESTABA EL ERROR DE LA IMAGEN:
        // Usamos ?? null para asegurar que undefined se convierta en null
        setFormData((prev) => ({
          ...prev,
          direccion: inst.direccion,
          lat: inst.ubicacion_lat ?? null, // Solución al error de la imagen
          lng: inst.ubicacion_long ?? null, // Solución al error de la imagen
          logoUrl: inst.logo || "",
        }));
      }
    };
    fetchInstitucion();
  }, [id]);

  // Handler del mapa (también con protección)
  const handleMapChange = (coords: {
    lat: number | null | undefined;
    lng: number | null | undefined;
  }) => {
    setFormData((prev) => ({
      ...prev,
      lat: coords.lat ?? null,
      lng: coords.lng ?? null,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.direccion.trim()) {
      setErrors({ direccion: "La dirección es obligatoria" });
      return;
    }

    setFormData((prev) => ({ ...prev, isSubmitting: true }));

    const payload = new FormData();
    payload.append("cue", cueRef.current?.value || "0");
    payload.append("nombre", nombreRef.current?.value || "");
    payload.append("direccion", formData.direccion);

    // Al enviar, si es null, mandamos el valor por defecto
    payload.append(
      "ubicacion_lat",
      (formData.lat ?? DEFAULT_COORDS.lat).toString()
    );
    payload.append(
      "ubicacion_long",
      (formData.lng ?? DEFAULT_COORDS.lng).toString()
    );

    payload.append("gestion", gestionRef.current?.value || Gestiones.Publica);

    if (cueanexoRef.current?.value)
      payload.append("cueanexo", cueanexoRef.current.value);
    if (telRef.current?.value) payload.append("tel", telRef.current.value);
    if (paginaRef.current?.value)
      payload.append("pagina", paginaRef.current.value);

    const logoFile = logoRef.current?.files?.[0];
    if (logoFile) payload.append("logo", logoFile);

    const api = new ApiResponse();
    await api.useFetch(
      id ? `instituciones/${id}` : "instituciones",
      id ? "PATCH" : "POST",
      payload,
      true
    );

    setFormData((prev) => ({ ...prev, isSubmitting: false }));

    if (!api.error) {
      toast({
        title: id ? "Actualizada" : "Creada",
        status: "success",
        duration: 2000,
      });
      navigate("/dashboard/instituciones");
    } else {
      handleApiError(api.error as unknown as ApiValidationResponse, toast);
    }
  };

  return (
    <APIProvider apiKey={config.API_MAPS_KEY}>
      <Box
        p={8}
        maxWidth="800px"
        mx="auto"
        bg="white"
        shadow="base"
        borderRadius="lg"
        mt={6}
      >
        <form onSubmit={handleSubmit}>
          <VStack spacing={6} align="stretch">
            <Heading size="md" textAlign="center">
              {id ? "Editar" : "Nueva"} Institución
            </Heading>

            <SimpleGrid columns={2} spacing={4}>
              <FormControl isRequired>
                <FormLabel>CUE</FormLabel>
                <Input type="number" ref={cueRef} />
              </FormControl>
              <FormControl>
                <FormLabel>CUE Anexo</FormLabel>
                <Input type="number" ref={cueanexoRef} />
              </FormControl>
            </SimpleGrid>

            <FormControl isRequired>
              <FormLabel>Nombre</FormLabel>
              <Input ref={nombreRef} />
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.direccion}>
              <FormLabel>Dirección</FormLabel>
              <Input
                value={formData.direccion}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, direccion: e.target.value }))
                }
              />
            </FormControl>

            <SimpleGrid columns={2} spacing={4}>
              <PhoneNumberInputArg telRef={telRef} />
              <UrlInput paginaRef={paginaRef} />
            </SimpleGrid>

            <FormControl isRequired>
              <FormLabel>Gestión</FormLabel>
              <Select ref={gestionRef}>
                <option value={Gestiones.Publica}>Pública</option>
                <option value={Gestiones.Privada}>Privada</option>
              </Select>
            </FormControl>

            <ImageFileInput inputRef={logoRef} label="Logo de la institución" />

            <Box
              h="300px"
              borderRadius="md"
              overflow="hidden"
              border="1px solid lightgray"
            >
              <MapComponent
                onDataFromMap={handleMapChange}
                lati={formData.lat ?? DEFAULT_COORDS.lat}
                lngi={formData.lng ?? DEFAULT_COORDS.lng}
              />
            </Box>

            <Button
              type="submit"
              colorScheme="teal"
              isLoading={formData.isSubmitting}
              width="full"
            >
              {id ? "Guardar cambios" : "Registrar"}
            </Button>
          </VStack>
        </form>
      </Box>
    </APIProvider>
  );
};

export default InstitucionFormPage;
